<template>
    <!-- Modal Backdrop -->
    <div 
        class="modal-backdrop fade" 
        :class="{ 'show': isVisible }"
        v-if="isVisible"
        @click="handleBackdropClick"
    ></div>

    <!-- Modal -->
    <div 
        class="modal fade" 
        :class="{ 'show d-block': isVisible }" 
        tabindex="-1" 
        role="dialog"
        v-show="isVisible"
    >
        <div class="modal-dialog modal-dialog-centered modal-lg" :class="modalSize">
            <div class="modal-content">
                <!-- Modal Header -->
                <div class="modal-header">
                    <h5 class="modal-title">
                        <slot name="title">{{ $t('passwordChange.title') }}</slot>
                    </h5>
                    <button 
                        type="button" 
                        class="btn-close" 
                        @click="closeModal"
                        aria-label="Close"
                    ></button>
                </div>

                <!-- Modal Body -->
                <div class="modal-body">
                    <slot name="body">
                        <form id="passwordChange" @submit.prevent="handleConfirm" autocomplete="off">
                            <div class="mb-3">
                                <label for="password" class="form-label">{{ $t('passwordChange.password') }}</label>
                                <input type="password" class="form-control" name="password" v-model="password" id="password" :placeholder="$t('passwordChange.password')" autocomplete="new-password" minlength="8" maxlength="50" required>
                            </div>
                            <div class="mb-3">
                                <label for="passwordConfirm" class="form-label">{{ $t('passwordChange.confirmPassword') }}</label>
                                <input type="password" class="form-control" name="passwordConfirm" v-model="passwordConfirm" id="passwordConfirm" :placeholder="$t('passwordChange.confirmPassword')" autocomplete="new-password" minlength="8" maxlength="50" required>
                            </div>
                        </form>
                    </slot>
                </div>

                <!-- Modal Footer -->
                <div class="modal-footer">
                    <slot name="footer">
                        <button
                            type="button"
                            class="btn btn-outline-secondary"
                            @click="closeModal"
                        >
                            {{ $t('passwordChange.cancel') }}
                        </button>
                        <button
                            type="submit"
                            form="passwordChange"
                            class="btn btn-primary"
                        >
                            {{ $t('passwordChange.submit') }}
                        </button>
                    </slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'BootstrapModal',
    props: {
        // Control visibility from parent
        modelValue: {
            type: Boolean,
            default: false
        },
        // Modal title
        title: {
            type: String,
            default: 'Modal Title'
        },
        // Modal size options: sm, lg, xl, or empty for default
        size: {
            type: String,
            default: '',
            validator: (value) => ['', 'sm', 'lg', 'xl'].includes(value)
        },
        // Close modal when clicking backdrop
        closeOnBackdrop: {
            type: Boolean,
            default: true
        },
        // Show close button in header
        showCloseButton: {
            type: Boolean,
            default: true
        },
        index: {
            type: Number,
            default: null
        }
    },
    emits: ['update:modelValue', 'close', 'confirm', 'opened'],
    data() {
        return {
            isVisible: this.modelValue,
            password: '',
            passwordConfirm: ''
        }
    },
    computed: {
        modalSize() {
            return this.size ? `modal-${this.size}` : ''
        }
    },
    watch: {
        modelValue(newVal) {
            this.isVisible = newVal
            if (newVal) {
                this.disableBodyScroll()
                this.$emit('opened')
            } else {
                this.enableBodyScroll()
            }
        },
        isVisible(newVal) {
        this.$emit('update:modelValue', newVal)
            if (!newVal) {
                this.$emit('close')
            }
        }
    },
    methods: {
        openModal() {
            this.isVisible = true
        },
        closeModal() {
            this.isVisible = false
        },
        handleConfirm() {
            this.$emit('confirm', { password: this.password, passwordConfirm: this.passwordConfirm })
        },
        handleBackdropClick() {
            if (this.closeOnBackdrop) {
                this.closeModal()
            }
        },
        disableBodyScroll() {
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = '15px' // Prevent content shift from scrollbar
        },
        enableBodyScroll() {
            document.body.style.overflow = 'auto'
            document.body.style.paddingRight = '0'
        },
        // Handle ESC key press
        handleEscapeKey(event) {
            if (event.key === 'Escape' && this.isVisible) {
                this.closeModal()
            }
        }
    },
    mounted() {
        // Add event listener for ESC key
        document.addEventListener('keydown', this.handleEscapeKey)
        
        if (this.isVisible) {
            this.disableBodyScroll()
        }
    },
    beforeUnmount() {
        // Clean up event listeners
        document.removeEventListener('keydown', this.handleEscapeKey)
        this.enableBodyScroll()
    }
}
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
}

.modal-backdrop {
  z-index: 1040;
}

.modal-body {
    padding: 3em;
}

.modal.show {
  opacity: 1;
}
</style>